import {
  applyEnvironmentPreset,
  getEnvironmentPreset,
  getEnvironmentPresetOptions
} from '../three/environment_presets.js?v=20260706d';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  DEFAULT_PROJECT_LEVEL_OPTIONS,
  DEFAULT_PROJECT_LEVELS,
  PROJECT_ENVIRONMENT_PRESET_KEYS,
  PROJECT_LEVEL_DOUBLE_CEILING_GRIDS,
  PROJECT_LEVEL_FLOOR_COLORS,
  PROJECT_LEVEL_FLOOR_GRID_SIZES,
  PROJECT_LEVEL_KEYS,
  normalizeProjectLevels
} from '../utils/common_constants.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import { recordUndo } from './history_service.js?v=20260903c';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { bindColorPicker, renderColorPicker } from '../../../colors/color_picker.js?v=20260720a';
import { colorPickerAllowedCatalogsForUsage } from '../../../colors/color_catalog_registry.js?v=20260720a';
import {
  OUTDOOR_SUN_TIMES,
  normalizeOutdoorSunSimulation
} from '../three/outdoor_sun_service.js';
import {
  normalizeOutdoorEnvironment,
  outdoorEnvironmentSourceLabel,
  normalizeOutdoorSunBrightness,
  outdoorEnvironmentToSunSimulation
} from '../three/outdoor_environment_service.js';

const GLOBAL_LEVEL_DEFINITIONS = Object.freeze([
  { key: 'dd', label: 'DD', title: 'Doppeldecke', inputId: 'levelDd', step: 10, colorClass: 'dd' },
  { key: 'dp', label: 'DP', title: 'Deckenpanel', inputId: 'levelDp', step: 5, colorClass: 'dp' },
  { key: 'rh', label: 'RH', title: 'Raumhöhe', inputId: 'levelRh', step: 10, colorClass: 'rh' },
  { key: 'db', label: 'DB', title: 'Doppelboden', inputId: 'levelDb', step: 5, colorClass: 'db' },
  { key: 'ub', label: 'UB', title: 'Unterboden', inputId: 'levelUb', step: 10, colorClass: 'ub' }
]);
const GLOBAL_LEVEL_BOUNDARY_SEGMENT_VALUE = 20;
const GLOBAL_LEVEL_BOUNDARY_MIN_HEIGHT = 20;
const GLOBAL_LEVEL_BAND_MIN_HEIGHT = 38;
const GLOBAL_LEVEL_INPUT_SEGMENT_MIN_HEIGHT = 28;
const GLOBAL_LEVEL_TECH_COMPACT_MIN_HEIGHT = 32;
const GLOBAL_LEVEL_CONTROL_MIN_HEIGHT = 42;
const GLOBAL_LEVEL_STACK_HEIGHT = 260;
const GLOBAL_LEVEL_VISUAL_DEFINITIONS = Object.freeze([
  { key: 'rd', label: 'RD', title: 'Rohe Decke', colorClass: 'rd', isBoundary: true, visualValue: GLOBAL_LEVEL_BOUNDARY_SEGMENT_VALUE },
  ...GLOBAL_LEVEL_DEFINITIONS,
  { key: 'outdoorSpace', label: '', title: 'Aussenbereich', colorClass: 'outdoor-space', isSpacer: true },
  { key: 'rb', label: 'RB', title: 'Rohboden', colorClass: 'rb', isBoundary: true, visualValue: GLOBAL_LEVEL_BOUNDARY_SEGMENT_VALUE }
]);
const GLOBAL_LEVEL_VISUAL_FILTERS = Object.freeze({
  outdoor: Object.freeze(['rb', 'outdoorSpace']),
  basement: Object.freeze(['rb', 'rh', 'rd']),
  datacenter: Object.freeze(['rb', 'ub', 'db', 'rh', 'dp', 'dd', 'rd']),
  custom: Object.freeze(['rb', 'ub', 'db', 'rh', 'dp', 'dd', 'rd'])
});
let outdoorLightingTimer = 0;
let pendingOutdoorLightingOptions = null;

/**
 * Rendert render Global Levels Panel im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function renderGlobalLevelsPanel() {
  const host = document.getElementById('levelsPanel');
  if (!host) return;

  syncGlobalRoomHeightControl();
  const button = document.getElementById('btnToggleLevels');
  if (button) {
    button.classList.remove('primary');
    button.classList.toggle('is-active', Boolean(S.ui.showLevelsPanel));
    button.setAttribute('aria-pressed', S.ui.showLevelsPanel ? 'true' : 'false');
  }

  if (!S.ui.showLevelsPanel) {
    host.className = 'hidden';
    host.innerHTML = '';
    return;
  }

  S.project.levels = normalizeProjectLevels(S.project.levels);
  const levels = S.project.levels;
  const environmentPreset = levels.environmentPreset || 'custom';
  const visibleKeys = visibleLevelKeysForPreset(environmentPreset);
  const stackSegments = GLOBAL_LEVEL_VISUAL_DEFINITIONS
    .filter(definition => visibleKeys.has(definition.key))
    .slice()
    .reverse()
    .map(definition => ({
    ...definition,
    value: definition.isSpacer
      ? 0
      : definition.isBoundary
      ? definition.visualValue
      : Math.max(0, Number(levels[definition.key]) || 0),
    options: levels.options?.[definition.key] || DEFAULT_PROJECT_LEVEL_OPTIONS[definition.key] || {}
  }));
  const total = stackSegments.reduce((sum, segment) => (
    segment.isBoundary ? sum : sum + segment.value
  ), 0);
  const stackHeight = levelVisualStackHeight(stackSegments);
  const active3dLevelKey = S.ui?.active3dLevelKey || null;

  host.className = '';
  host.innerHTML = `
    <div class="card levels-card">
      <div class="card-title">${escapeLevelHtml(t('sidebar_rechts.ebenen.titel.karte', 'Ebenen'))}</div>
      ${renderEnvironmentPanel(levels, environmentPreset)}
      <div class="levels-editor">
        <div class="levels-visual">
          <div class="levels-stack" aria-label="Raumquerschnitt" style="--levels-stack-height:${stackHeight}px">
            ${stackSegments.map(segment => `
              <div
                class="levels-segment ${segment.colorClass}${isLevelBoundaryBand(segment.key) ? ' separator-band' : ''}${segment.isBoundary ? ' is-boundary' : ''}${segment.isSpacer ? ' is-spacer' : ''}${segment.options.includeInCalculation === false ? ' is-calculation-disabled' : ''}${segment.options.showIn3d ? ' is-3d-enabled' : ''}${active3dLevelKey === segment.key ? ' is-3d-selected' : ''}"
                data-level-key="${segment.key}"
                style="flex:${levelVisualSegmentFlex(segment)}"
                title="${segment.isBoundary || segment.isSpacer ? segment.title : `${segment.title}: ${Math.round(segment.value)} mm`}"
              >
                <span class="levels-segment-main">
                  <span class="levels-segment-label">${segment.label}</span>
                </span>
                ${renderLevelSegmentControls(segment, levels)}
              </div>
            `).join('')}
          </div>
          <div class="small-text note">${escapeLevelHtml(t('sidebar_rechts.ebenen.label.gesamt', 'Gesamt'))}: ${Math.round(total)} mm</div>
        </div>
      </div>
      ${renderLevelDetailOptions(levels)}
      ${renderLevelProfileReset(levels)}
    </div>
    ${environmentPreset === 'outdoor' ? renderOutdoorEnvironmentCard() : ''}
  `;

  bindGlobalLevelControls();
  bindGlobalLevelTexturePickers();
}

// bindColorPicker attaches listeners directly to each picker's own DOM
// nodes (not event-delegated from a stable ancestor like the rest of this
// panel), so — unlike bindGlobalLevelControls — this needs to run again
// after every re-render, not just once.
function bindGlobalLevelTexturePickers() {
  const fields = [
    ['levelFloorTexturePicker', 'floorTexture', 'Boden'],
    ['levelCeilingTexturePicker', 'ceilingTexture', 'Decke'],
    ['levelDoubleFloorTexturePicker', 'doubleFloorTexture', 'Doppelboden'],
    ['levelDoubleCeilingTexturePicker', 'doubleCeilingTexture', 'Doppeldecke']
  ];
  for (const [pickerId, field, label] of fields) {
    if (!document.getElementById(pickerId)) continue;
    bindColorPicker(pickerId, colorValue => updateProjectLevelTextureField(field, colorValue, label));
  }
}

function renderEnvironmentPanel(levels, environmentPreset) {
  const presets = orderedEnvironmentPresets();
  return `
    <div class="levels-environment-panel">
      <div class="level-preset-strip" aria-label="Umgebungs-Presets">
        ${presets.map(preset => `
          <button
            type="button"
            class="level-option-button level-preset-button${environmentPreset === preset.id ? ' is-active' : ''}"
            data-environment-preset="${preset.id}"
            title="${escapeLevelHtml(preset.description || preset.label)}"
          >
            ${escapeLevelHtml(preset.label)}
          </button>
        `).join('')}
      </div>
    </div>
  `;
}

function orderedEnvironmentPresets() {
  const order = ['outdoor', 'datacenter', 'basement', 'custom'];
  const byId = new Map(getEnvironmentPresetOptions().map(preset => [preset.id, preset]));
  return order.map(id => byId.get(id)).filter(Boolean);
}

function visibleLevelKeysForPreset(presetId) {
  const key = String(presetId || 'custom').toLowerCase();
  const visible = GLOBAL_LEVEL_VISUAL_FILTERS[key] || GLOBAL_LEVEL_VISUAL_FILTERS.custom;
  return new Set(visible);
}

function projectLevelDefaultProfile(presetId) {
  const key = PROJECT_ENVIRONMENT_PRESET_KEYS.includes(String(presetId || '').toLowerCase())
    ? String(presetId).toLowerCase()
    : DEFAULT_PROJECT_LEVELS.environmentPreset;
  const preset = getEnvironmentPreset(key);
  const optionDefaults = {};
  for (const levelKey of PROJECT_LEVEL_KEYS) {
    optionDefaults[levelKey] = {
      ...(DEFAULT_PROJECT_LEVEL_OPTIONS[levelKey] || {}),
      ...(preset?.options?.[levelKey] || {})
    };
  }
  return projectLevelProfileSnapshot(normalizeProjectLevels({
    ...DEFAULT_PROJECT_LEVELS,
    ...(preset?.levels || {}),
    options: optionDefaults,
    environmentPreset: key
  }), key);
}

function projectLevelProfileSnapshot(levels, presetId) {
  const normalized = normalizeProjectLevels(levels);
  const key = PROJECT_ENVIRONMENT_PRESET_KEYS.includes(String(presetId || normalized.environmentPreset || '').toLowerCase())
    ? String(presetId || normalized.environmentPreset).toLowerCase()
    : DEFAULT_PROJECT_LEVELS.environmentPreset;
  const options = {};
  for (const levelKey of PROJECT_LEVEL_KEYS) {
    options[levelKey] = {
      includeInCalculation: normalized.options?.[levelKey]?.includeInCalculation !== false,
      showIn3d: normalized.options?.[levelKey]?.showIn3d === true
    };
  }
  return {
    ub: normalized.ub,
    db: normalized.db,
    rh: normalized.rh,
    dp: normalized.dp,
    dd: normalized.dd,
    options,
    floorGridSize: normalized.floorGridSize,
    floorColor: normalized.floorColor,
    doubleCeilingGrid: normalized.doubleCeilingGrid,
    floorType: normalized.floorType,
    ceilingType: normalized.ceilingType,
    environmentPreset: key,
    bo: normalized.db,
    dc: normalized.dp
  };
}

function projectLevelProfilesWithActive(levels) {
  const normalized = normalizeProjectLevels(levels);
  return {
    ...(normalized.profiles || {}),
    [normalized.environmentPreset || 'custom']: projectLevelProfileSnapshot(normalized, normalized.environmentPreset || 'custom')
  };
}

function syncActiveProjectLevelProfile() {
  const levels = normalizeProjectLevels(S.project.levels);
  const profiles = projectLevelProfilesWithActive(levels);
  S.project.levels = normalizeProjectLevels({
    ...levels,
    profiles
  });
}

function projectLevelProfileModified(levels) {
  const normalized = normalizeProjectLevels(levels);
  const activeKey = normalized.environmentPreset || 'custom';
  if (activeKey === 'custom') return false;
  return !projectLevelProfilesEqual(
    projectLevelProfileSnapshot(normalized, activeKey),
    projectLevelDefaultProfile(activeKey)
  );
}

function projectLevelProfilesEqual(left, right) {
  const a = projectLevelProfileSnapshot(left, left?.environmentPreset);
  const b = projectLevelProfileSnapshot(right, right?.environmentPreset);
  for (const key of ['ub', 'db', 'dp', 'dd', 'floorGridSize']) {
    if (Math.round(a[key]) !== Math.round(b[key])) return false;
  }
  for (const key of ['floorColor', 'doubleCeilingGrid', 'floorType', 'ceilingType']) {
    if (String(a[key]) !== String(b[key])) return false;
  }
  for (const levelKey of PROJECT_LEVEL_KEYS) {
    if ((a.options[levelKey]?.includeInCalculation !== false) !== (b.options[levelKey]?.includeInCalculation !== false)) return false;
    if ((a.options[levelKey]?.showIn3d === true) !== (b.options[levelKey]?.showIn3d === true)) return false;
  }
  return true;
}

function isLevelBoundaryBand(key) {
  return ['rb', 'db', 'dp', 'rd'].includes(key);
}

function isCompactTechnicalLevel(key) {
  return ['db'].includes(key);
}

function isInputHeightTechnicalLevel(key) {
  return ['db', 'dp'].includes(key);
}

function levelVisualStackHeight(segments) {
  return Math.max(GLOBAL_LEVEL_STACK_HEIGHT, segments.reduce((sum, segment) => (
    sum + levelVisualSegmentMinHeight(segment)
  ), 0));
}

function levelVisualSegmentMinHeight(segment) {
  if (segment?.isSpacer) return 0;
  if (segment?.isBoundary) return GLOBAL_LEVEL_BOUNDARY_MIN_HEIGHT;
  if (isInputHeightTechnicalLevel(segment?.key)) return GLOBAL_LEVEL_INPUT_SEGMENT_MIN_HEIGHT;
  if (isCompactTechnicalLevel(segment?.key)) return GLOBAL_LEVEL_TECH_COMPACT_MIN_HEIGHT;
  if (isLevelBoundaryBand(segment?.key)) return GLOBAL_LEVEL_BAND_MIN_HEIGHT;
  return GLOBAL_LEVEL_CONTROL_MIN_HEIGHT;
}

function levelVisualSegmentFlex(segment) {
  if (segment?.key === 'rh' || segment?.isSpacer) return '1 1 0';
  return `0 0 ${levelVisualSegmentMinHeight(segment)}px`;
}

function renderLevelTexturePickerHtml(id, label, value) {
  return `
    <div class="level-detail-texture-field">
      ${label ? `<span class="level-detail-texture-label">${escapeLevelHtml(label)}</span>` : ''}
      ${renderColorPicker({
        id,
        value,
        allowedCatalogs: colorPickerAllowedCatalogsForUsage(id),
        allowDefault: true,
        defaultLabel: 'Standard'
      })}
    </div>
  `;
}

// Boden/Decke/Doppelboden/Doppeldecke each collapse behind a header — with
// 100x100 swatches (see colors/color_picker.js) showing all 4 at once would
// make the Ebenen panel unreasonably tall. Expanded state lives in
// S.ui.levelsExpandedSections, toggled via the delegated click handler in
// bindGlobalLevelControls.
function renderLevelCollapsibleSection(key, label, contentHtml) {
  const expanded = Boolean(S.ui?.levelsExpandedSections?.[key]);
  return `
    <div class="level-detail-collapsible${expanded ? ' is-expanded' : ''}">
      <button type="button" class="level-detail-collapsible-toggle" data-level-collapsible-toggle="${key}" aria-expanded="${expanded ? 'true' : 'false'}">
        <span>${escapeLevelHtml(label)}</span>
        <span class="level-detail-collapsible-caret" aria-hidden="true">${expanded ? '−' : '+'}</span>
      </button>
      ${expanded ? `<div class="level-detail-collapsible-body">${contentHtml}</div>` : ''}
    </div>
  `;
}

function renderLevelDetailSelect(field, label, value, options) {
  return `
    <label>${label}
      <select name="levelDetail-${escapeLevelHtml(field)}" data-level-detail-field="${field}">
        ${options.map(option => `<option value="${option.key}" ${String(value) === String(option.key) ? 'selected' : ''}>${escapeLevelHtml(option.label)}</option>`).join('')}
      </select>
    </label>
  `;
}

function renderLevelSegmentControls(definition, levels) {
  if (definition.isBoundary || definition.isSpacer || !definition.inputId) return '';
  const options = levels.options?.[definition.key] || DEFAULT_PROJECT_LEVEL_OPTIONS[definition.key];
  const value = Math.round(Math.max(0, Number(levels[definition.key]) || 0));
  const calcId = `${definition.inputId}Calc`;
  const isCustomProfile = (levels.environmentPreset || 'custom') === 'custom';
  return `
    <div class="level-segment-controls${isCustomProfile ? ' has-checks' : ''}" title="${escapeLevelHtml(definition.title)}">
      <input id="${definition.inputId}" class="level-value-input level-segment-input" type="number" min="0" step="${definition.step}" value="${value}" placeholder="${definition.label}" aria-label="${escapeLevelHtml(definition.title)}" title="${escapeLevelHtml(definition.title)}">
      ${isCustomProfile ? `
      <span class="level-segment-checks">
        <label class="level-mini-check" title="${escapeLevelHtml(definition.title)} aktivieren">
          <input id="${calcId}" type="checkbox" data-level-option="${definition.key}" data-option-field="includeInCalculation" aria-label="${escapeLevelHtml(definition.title)} aktivieren" ${options.includeInCalculation !== false ? 'checked' : ''}>
          <span>${escapeLevelHtml(t('sidebar_rechts.ebenen.label.aktiv', 'Aktiv'))}</span>
        </label>
      </span>
      ` : ''}
    </div>
  `;
}

function renderLevelDetailOptions(levels) {
  const gridSize = PROJECT_LEVEL_FLOOR_GRID_SIZES.includes(Number(levels.floorGridSize)) ? Number(levels.floorGridSize) : 600;
  const doubleCeilingGrid = PROJECT_LEVEL_DOUBLE_CEILING_GRIDS[levels.doubleCeilingGrid]
    ? levels.doubleCeilingGrid
    : DEFAULT_PROJECT_LEVELS.doubleCeilingGrid;
  const visibleKeys = visibleLevelKeysForPreset(levels.environmentPreset || 'custom');
  const showCeiling = visibleKeys.has('rd');
  const showDoubleCeiling = visibleKeys.has('dd');
  const showDoubleFloor = visibleKeys.has('db');
  const showFloor = visibleKeys.has('rb');
  return `
    <div class="levels-detail-options">
      <div class="level-detail-selects">
        ${showCeiling ? renderLevelCollapsibleSection('decke', 'Decke', renderLevelTexturePickerHtml('levelCeilingTexturePicker', '', levels.ceilingTexture)) : ''}
        ${showDoubleCeiling ? renderLevelCollapsibleSection('doppeldecke', 'Doppeldecke', `
          ${renderLevelDetailSelect('doubleCeilingGrid', escapeLevelHtml(t('sidebar_rechts.ebenen.label.raster', 'Raster')), doubleCeilingGrid, Object.entries(PROJECT_LEVEL_DOUBLE_CEILING_GRIDS).map(([key, config]) => ({ key, label: config.label })))}
          ${renderLevelTexturePickerHtml('levelDoubleCeilingTexturePicker', '', levels.doubleCeilingTexture)}
        `) : ''}
        ${showDoubleFloor ? renderLevelCollapsibleSection('doppelboden', 'Doppelboden', `
          ${renderLevelDetailSelect('floorGridSize', escapeLevelHtml(t('sidebar_rechts.ebenen.label.raster', 'Raster')), gridSize, PROJECT_LEVEL_FLOOR_GRID_SIZES.map(size => ({ key: size, label: `${size}x${size}` })))}
          ${renderDoubleFloorColorOptions(levels)}
          ${renderLevelTexturePickerHtml('levelDoubleFloorTexturePicker', '', levels.doubleFloorTexture)}
        `) : ''}
        ${showFloor ? renderLevelCollapsibleSection('boden', 'Boden', renderLevelTexturePickerHtml('levelFloorTexturePicker', '', levels.floorTexture)) : ''}
      </div>
    </div>
  `;
}

function renderLevelProfileReset(levels) {
  if (!projectLevelProfileModified(levels)) return '';
  return `
    <div class="levels-profile-reset">
      <button type="button" class="btn" data-reset-level-profile>auf Standard zurücksetzen</button>
    </div>
  `;
}

function renderDoubleFloorColorOptions(levels) {
  const floorColor = PROJECT_LEVEL_FLOOR_COLORS[levels.floorColor] ? levels.floorColor : 'gray';
  return `
    <div class="level-extra-options level-floor-options">
      <div class="level-color-strip" aria-label="Doppelboden Farbe" title="Doppelboden Farbe">
        ${Object.entries(PROJECT_LEVEL_FLOOR_COLORS).map(([key, config]) => `
          <button
            type="button"
            class="level-color-button${floorColor === key ? ' is-active' : ''}"
            data-floor-color="${key}"
            title="${config.label}"
            aria-label="${config.label}"
            style="--level-color:${config.color}"
          ></button>
        `).join('')}
      </div>
    </div>
  `;
}

function bindGlobalLevelControls() {
  const host = document.getElementById('levelsPanel');
  if (!host || host.dataset.globalLevelControlsBound === 'true') return;
  host.dataset.globalLevelControlsBound = 'true';

  host.addEventListener('click', event => {
    const floorColorButton = event.target.closest?.('[data-floor-color]');
    if (floorColorButton) {
      const value = String(floorColorButton.dataset.floorColor || '');
      if (!PROJECT_LEVEL_FLOOR_COLORS[value]) return;
      recordUndo('Doppelbodenfarbe ändern');
      S.project.levels = normalizeProjectLevels(S.project.levels);
      S.project.levels.floorColor = value;
      AppRuntime.requestAppRefresh(false);
      return;
    }

    const presetButton = event.target.closest?.('[data-environment-preset]');
    if (presetButton) {
      updateProjectLevelVisualFilter(presetButton.dataset.environmentPreset);
      return;
    }

    if (event.target.closest?.('[data-reset-level-profile]')) {
      resetActiveProjectLevelProfile();
      return;
    }

    const collapsibleToggle = event.target.closest?.('[data-level-collapsible-toggle]');
    if (collapsibleToggle) {
      const key = collapsibleToggle.dataset.levelCollapsibleToggle;
      S.ui.levelsExpandedSections ||= {};
      S.ui.levelsExpandedSections[key] = !S.ui.levelsExpandedSections[key];
      AppRuntime.requestAppRefresh(false);
      return;
    }

    const sunTimeButton = event.target.closest?.('[data-outdoor-sun-time]');
    if (sunTimeButton) updateOutdoorSunSimulationTime(sunTimeButton.dataset.outdoorSunTime);
  });

  host.addEventListener('input', event => {
    const input = event.target.closest?.('[data-outdoor-sun-brightness]');
    if (!input) return;
    updateOutdoorSunBrightness(input.value, {
      brightnessOnly: true,
      lightingOnly: true,
      recordUndo: false,
      refresh: false,
      renderControls: false,
      setStatus: false
    });
    const output = input.closest('.outdoor-sun-brightness')?.querySelector('[data-outdoor-sun-brightness-value]');
    if (output) output.textContent = `${normalizeOutdoorSunBrightness(input.value)}%`;
  });

  host.addEventListener('change', event => {
    const target = event.target;
    const levelDefinition = GLOBAL_LEVEL_DEFINITIONS.find(definition => definition.inputId === target.id);
    if (levelDefinition) {
      const value = Math.max(0, Number.isFinite(Number(target.value)) ? Number(target.value) : 0);
      updateProjectLevelValue(levelDefinition, value);
      return;
    }
    if (target.matches?.('[data-level-option]')) {
      updateProjectLevelOption(target.dataset.levelOption, target.dataset.optionField, target.checked);
      return;
    }
    if (target.matches?.('[data-level-detail-field]')) {
      updateProjectLevelDetailField(target.dataset.levelDetailField, target.value);
      return;
    }
    if (target.matches?.('[data-outdoor-sun-brightness]')) {
      updateOutdoorSunBrightness(target.value, { recordUndo: false, renderControls: true, setStatus: true });
    }
  });
}

function renderScenePresetControls(root = document) {
  const host = root?.querySelector?.('#scenePresetControls') || document.getElementById('scenePresetControls');
  if (!host) return;
  const levels = normalizeProjectLevels(S.project.levels);
  const activePreset = AppRuntime.current3DModalScenePreset()
    || levels.environmentPreset
    || 'custom';
  const presets = orderedEnvironmentPresets();
  host.innerHTML = `
    <div class="scene-preset-strip" aria-label="Szenenfilter">
      ${presets.map(preset => `
        <button
          type="button"
          class="scene-preset-button${activePreset === preset.id ? ' is-active' : ''}"
          data-scene-preset="${preset.id}"
          aria-pressed="${activePreset === preset.id ? 'true' : 'false'}"
          title="${escapeLevelHtml(preset.description || preset.label)}"
        >${escapeLevelHtml(preset.label)}</button>
      `).join('')}
    </div>
  `;
  host.querySelectorAll('[data-scene-preset]').forEach(button => {
    button.addEventListener('click', event => {
      AppRuntime.set3DModalScenePreset(event.currentTarget.dataset.scenePreset);
    });
  });
  const sunHost = root?.querySelector?.('#outdoorSunModalCard') || document.getElementById('outdoorSunModalCard');
  if (sunHost) {
    sunHost.classList.remove('hidden');
    sunHost.innerHTML = renderOutdoorEnvironmentControls({
      compact: activePreset !== 'outdoor',
      brightnessOnly: activePreset !== 'outdoor'
    });
  }
  bindOutdoorSunControls(host);
  bindOutdoorSunControls(sunHost);
}

function renderOutdoorEnvironmentCard() {
  return `
    <div class="card outdoor-sun-card">
      <div class="card-title">${escapeLevelHtml(t('sidebar_rechts.aussenbereich.titel.karte', 'Aussenbereich'))}</div>
      ${renderOutdoorEnvironmentControls()}
    </div>
  `;
}

function renderOutdoorEnvironmentControls(options = {}) {
  const env = normalizeOutdoorEnvironment(S.project?.outdoorEnvironment, S.project?.floorplan, S.project?.outdoorSunSimulation);
  const compactClass = options.compact ? ' is-compact' : '';
  const brightnessOnly = options.brightnessOnly === true;
  const sunBrightness = normalizeOutdoorSunBrightness(env.sunBrightness);
  return `
    <div class="outdoor-sun-controls${compactClass}" aria-label="${brightnessOnly ? 'Szenenhelligkeit' : 'Sonnenstand'}">
      ${brightnessOnly ? '' : `
        <span class="outdoor-sun-label">${options.compact ? escapeLevelHtml(t('sidebar_rechts.aussenbereich.label.sonnenstand', 'Sonnenstand')) : escapeLevelHtml(t('sidebar_rechts.aussenbereich.label.uhrzeit', 'Uhrzeit'))}</span>
        <div class="outdoor-sun-buttons">
          ${OUTDOOR_SUN_TIMES.map(time => `
            <button
              type="button"
              class="level-option-button outdoor-sun-button${env.sunTime === time ? ' is-active' : ''}"
              data-outdoor-sun-time="${time}"
              aria-pressed="${env.sunTime === time ? 'true' : 'false'}"
            >${escapeLevelHtml(time)}</button>
          `).join('')}
        </div>
      `}
      <label class="outdoor-sun-brightness">
        <span>${escapeLevelHtml(t('sidebar_rechts.aussenbereich.label.helligkeit', 'Helligkeit'))}</span>
        <input
          type="range"
          min="40"
          max="180"
          step="10"
          value="${sunBrightness}"
          data-outdoor-sun-brightness
          aria-label="Sonnenhelligkeit"
        >
        <output data-outdoor-sun-brightness-value>${sunBrightness}%</output>
      </label>
      ${brightnessOnly ? '' : `<span class="outdoor-sun-source">${escapeLevelHtml(outdoorEnvironmentSourceLabel(env))}</span>`}
    </div>
  `;
}

function bindOutdoorSunControls(root = document) {
  if (!root) return;
  root.querySelectorAll?.('[data-outdoor-sun-time]').forEach(button => {
    button.addEventListener('click', event => {
      updateOutdoorSunSimulationTime(event.currentTarget.dataset.outdoorSunTime);
    });
  });
  root.querySelectorAll?.('[data-outdoor-sun-brightness]').forEach(input => {
    input.addEventListener('input', event => {
      updateOutdoorSunBrightness(event.currentTarget.value, {
        brightnessOnly: true,
        lightingOnly: true,
        recordUndo: false,
        refresh: false,
        renderControls: false,
        setStatus: false
      });
      const output = event.currentTarget.closest('.outdoor-sun-brightness')?.querySelector('[data-outdoor-sun-brightness-value]');
      if (output) output.textContent = `${normalizeOutdoorSunBrightness(event.currentTarget.value)}%`;
    });
    input.addEventListener('change', event => {
      updateOutdoorSunBrightness(event.currentTarget.value, { recordUndo: false, renderControls: true, setStatus: true });
    });
  });
}

function beginProjectLevelFieldChange(field, previousValue, nextValue, previousLevels = null) {
  if (previousValue === nextValue) return null;
  const context = {
    field,
    previousValue,
    nextValue,
    previousLevels: previousLevels && typeof previousLevels === 'object' ? { ...previousLevels } : null
  };
  SystemRegistry.beforeProjectLevelsChange(context);
  return context;
}

function finishProjectLevelFieldChange(context, nextLevels = null) {
  if (!context) return false;
  context.nextLevels = nextLevels && typeof nextLevels === 'object' ? { ...nextLevels } : null;
  if (context.nextLevels && Object.prototype.hasOwnProperty.call(context.nextLevels, context.field)) {
    context.nextValue = context.nextLevels[context.field];
  }
  return SystemRegistry.afterProjectLevelsChange(context) === true;
}

function sync3DModalScenePresetWithEnvironment(key) {
  if (!S.view3d || typeof S.view3d !== 'object') return;
  S.view3d.modalScenePreset = key;
  S.view3d.modalBackgroundMode = null;
  renderScenePresetControls(document.getElementById('threeDModalRoot'));
}

function updateProjectLevelVisualFilter(presetId, options = {}) {
  const key = String(presetId || '').trim().toLowerCase();
  if (!GLOBAL_LEVEL_VISUAL_FILTERS[key]) return;
  const current = normalizeProjectLevels(S.project.levels);
  if (current.environmentPreset === key) {
    sync3DModalScenePresetWithEnvironment(key);
    if (key === 'outdoor') normalizeProjectOutdoorEnvironment();
    return;
  }
  const preset = getEnvironmentPreset(key);
  recordUndo(`Ebenenfilter ${preset?.label || key}`);
  const profiles = projectLevelProfilesWithActive(current);
  const targetProfile = options.loadProfile === false
    ? projectLevelProfileSnapshot(current, key)
    : (profiles[key] || projectLevelDefaultProfile(key));
  const next = normalizeProjectLevels({
    ...targetProfile,
    rh: current.rh,
    environmentPreset: key,
    profiles
  });
  const roomHeightChange = beginProjectLevelFieldChange('rh', current.rh, next.rh, current);
  S.project.levels = next;
  if (key === 'outdoor') normalizeProjectOutdoorEnvironment();
  sync3DModalScenePresetWithEnvironment(key);
  finishProjectLevelFieldChange(roomHeightChange, S.project.levels);
  AppRuntime.requestAppRefresh(false);
}

function updateOutdoorSunSimulationTime(time) {
  if (!OUTDOOR_SUN_TIMES.includes(String(time || '').trim())) return;
  const current = normalizeOutdoorEnvironment(S.project.outdoorEnvironment, S.project.floorplan, S.project.outdoorSunSimulation);
  const next = normalizeOutdoorEnvironment({
    ...current,
    enabled: true,
    sunTime: String(time).trim()
  }, S.project.floorplan, S.project.outdoorSunSimulation);
  const changed = current.sunTime !== next.sunTime
    || current.locationSource !== next.locationSource
    || Math.abs((Number(current.latitude) || 0) - (Number(next.latitude) || 0)) > 0.000001
    || Math.abs((Number(current.longitude) || 0) - (Number(next.longitude) || 0)) > 0.000001
    || current.enabled !== next.enabled;
  if (!changed) return;
  applyOutdoorEnvironmentUpdate(next, `Sonnenstand ${next.sunTime}`, {
    refresh: false,
    lightingOnly: true,
    outdoorEnvironmentOnly: true,
    skipShadowUpdate: true
  });
}

function updateOutdoorSunBrightness(value, options = {}) {
  const brightness = normalizeOutdoorSunBrightness(value);
  const current = normalizeOutdoorEnvironment(S.project.outdoorEnvironment, S.project.floorplan, S.project.outdoorSunSimulation);
  const next = normalizeOutdoorEnvironment({
    ...current,
    enabled: true,
    sunBrightness: brightness
  }, S.project.floorplan, S.project.outdoorSunSimulation);
  if (current.sunBrightness === next.sunBrightness && current.enabled === next.enabled) return;
  const activePreset = AppRuntime.current3DModalScenePreset?.() || normalizeProjectLevels(S.project.levels).environmentPreset || 'custom';
  const outdoorActive = activePreset === 'outdoor';
  applyOutdoorEnvironmentUpdate(next, `${outdoorActive ? 'Sonnenhelligkeit' : '3D-Helligkeit'} ${brightness}%`, {
    ...options,
    statusText: outdoorActive
      ? `Aussenbereich · ${next.sunTime} · ${outdoorEnvironmentSourceLabel(next)}`
      : `3D-Helligkeit ${brightness}%`
  });
}

function normalizeProjectOutdoorEnvironment() {
  S.project.outdoorEnvironment = normalizeOutdoorEnvironment(S.project.outdoorEnvironment, S.project.floorplan, S.project.outdoorSunSimulation);
  S.project.outdoorSunSimulation = normalizeOutdoorSunSimulation(S.project.outdoorSunSimulation, S.project.floorplan);
  S.project.outdoorSunSimulation = {
    ...S.project.outdoorSunSimulation,
    ...outdoorEnvironmentToSunSimulation(S.project.outdoorEnvironment)
  };
}

function applyOutdoorEnvironmentUpdate(next, undoLabel, options = {}) {
  if (options.recordUndo !== false) recordUndo(undoLabel);
  S.project.outdoorEnvironment = next;
  S.project.outdoorSunSimulation = {
    ...normalizeOutdoorSunSimulation(S.project.outdoorSunSimulation, S.project.floorplan),
    ...outdoorEnvironmentToSunSimulation(next)
  };
  if (options.setStatus !== false) setStatus(options.statusText || `Aussenbereich · ${next.sunTime} · ${outdoorEnvironmentSourceLabel(next)}`);
  if (options.renderControls !== false) {
    renderGlobalLevelsPanel();
    renderScenePresetControls(document.getElementById('threeDModalRoot'));
  }
  if (options.refresh !== false) AppRuntime.requestAppRefresh(false);
  if (options.lightingOnly === true) {
    scheduleOutdoorLightingUpdate({
      brightnessOnly: options.brightnessOnly === true
    });
  } else {
    AppRuntime.render3D?.({ preserveCamera: true });
  }
}

function scheduleOutdoorLightingUpdate(options = {}) {
  pendingOutdoorLightingOptions = {
    ...(pendingOutdoorLightingOptions || {}),
    ...options
  };
  if (outdoorLightingTimer) return;
  outdoorLightingTimer = window.setTimeout(() => {
    outdoorLightingTimer = 0;
    const nextOptions = pendingOutdoorLightingOptions || {};
    pendingOutdoorLightingOptions = null;
    AppRuntime.update3DLighting?.(nextOptions);
  }, 32);
}

function updateProjectLevelDetailField(field, value) {
  S.project.levels = normalizeProjectLevels(S.project.levels);

  if (field === 'floorGridSize') {
    const next = Number(value);
    if (!PROJECT_LEVEL_FLOOR_GRID_SIZES.includes(next) || S.project.levels.floorGridSize === next) return;
    recordUndo('Doppelboden-Raster ändern');
    S.project.levels.floorGridSize = next;
    syncActiveProjectLevelProfile();
    AppRuntime.requestAppRefresh(false);
    return;
  }

  if (field === 'doubleCeilingGrid') {
    const next = String(value || '').trim().toLowerCase();
    if (!PROJECT_LEVEL_DOUBLE_CEILING_GRIDS[next] || S.project.levels.doubleCeilingGrid === next) return;
    recordUndo('Doppeldecken-Raster ändern');
    S.project.levels.doubleCeilingGrid = next;
    syncActiveProjectLevelProfile();
    AppRuntime.requestAppRefresh(false);
  }
}

// Selected Texturen-catalog color for Boden/Decke/Wand/Doppelboden/
// Doppeldecke — additive to the legacy floorType/ceilingType enum fields,
// so existing projects render exactly as before until a texture is picked.
function updateProjectLevelTextureField(field, colorValue, label) {
  recordUndo(`${label} ändern`);
  S.project.levels = normalizeProjectLevels(S.project.levels);
  S.project.levels[field] = colorValue || null;
  syncActiveProjectLevelProfile();
  AppRuntime.requestAppRefresh(false);
}

function applyProjectEnvironmentPreset(presetId) {
  const preset = getEnvironmentPreset(presetId);
  if (!preset) return;
  recordUndo(`Umgebung ${preset.label} anwenden`);
  const previous = normalizeProjectLevels(S.project.levels);
  const nextRh = previous.rh;
  const roomHeightChange = beginProjectLevelFieldChange('rh', previous.rh, nextRh, previous);
  applyEnvironmentPreset(S, preset.id);
  const current = normalizeProjectLevels(S.project.levels);
  finishProjectLevelFieldChange(roomHeightChange, current);
  setStatus(`Umgebung ${preset.label} angewendet`);
  AppRuntime.requestAppRefresh(false);
}

function escapeLevelHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));
}

function updateProjectLevelValue(definition, value) {
  recordUndo(`${definition.title} ändern`);
  S.project.levels = normalizeProjectLevels(S.project.levels);
  const levelChange = beginProjectLevelFieldChange(definition.key, S.project.levels[definition.key], value, S.project.levels);
  S.project.levels[definition.key] = value;
  if (definition.key === 'db') S.project.levels.bo = value;
  if (definition.key === 'dp') S.project.levels.dc = value;
  S.project.levels = normalizeProjectLevels(S.project.levels);
  finishProjectLevelFieldChange(levelChange, S.project.levels);
  syncActiveProjectLevelProfile();
  AppRuntime.requestAppRefresh(false);
}

function updateProjectLevelOption(key, field, value) {
  if (!PROJECT_LEVEL_KEYS.includes(key) || !['includeInCalculation', 'showIn3d'].includes(field)) return;
  const definition = GLOBAL_LEVEL_DEFINITIONS.find(item => item.key === key);
  recordUndo(field === 'includeInCalculation'
    ? `${definition?.title || key} Berechnung ändern`
    : `${definition?.title || key} 3D-Anzeige ändern`);
  S.project.levels = normalizeProjectLevels(S.project.levels);
  S.project.levels.options[key][field] = Boolean(value);
  S.project.levels = normalizeProjectLevels(S.project.levels);
  syncActiveProjectLevelProfile();
  AppRuntime.requestAppRefresh(false);
}

function resetActiveProjectLevelProfile() {
  const current = normalizeProjectLevels(S.project.levels);
  const activeKey = current.environmentPreset || 'custom';
  const profiles = {
    ...(current.profiles || {}),
    [activeKey]: projectLevelDefaultProfile(activeKey)
  };
  const next = normalizeProjectLevels({
    ...profiles[activeKey],
    rh: current.rh,
    environmentPreset: activeKey,
    profiles
  });
  recordUndo('Ebenenprofil auf Standard zurücksetzen');
  const roomHeightChange = beginProjectLevelFieldChange('rh', current.rh, next.rh, current);
  S.project.levels = next;
  finishProjectLevelFieldChange(roomHeightChange, S.project.levels);
  AppRuntime.requestAppRefresh(false);
}

/**
 * Beschreibt sync Global Room Height Control im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
function syncGlobalRoomHeightControl() {
  const input = document.getElementById('inputRoomHeight');
  if (!input) return;
  const levels = normalizeProjectLevels(S.project.levels);
  const value = String(Math.round(levels.rh));
  if (input.value !== value) input.value = value;
}

const GlobalLevels = Object.freeze({
  GLOBAL_LEVEL_DEFINITIONS,
  GLOBAL_LEVEL_BOUNDARY_SEGMENT_VALUE,
  GLOBAL_LEVEL_BOUNDARY_MIN_HEIGHT,
  GLOBAL_LEVEL_BAND_MIN_HEIGHT,
  GLOBAL_LEVEL_INPUT_SEGMENT_MIN_HEIGHT,
  GLOBAL_LEVEL_TECH_COMPACT_MIN_HEIGHT,
  GLOBAL_LEVEL_CONTROL_MIN_HEIGHT,
  GLOBAL_LEVEL_STACK_HEIGHT,
  GLOBAL_LEVEL_VISUAL_DEFINITIONS,
  GLOBAL_LEVEL_VISUAL_FILTERS,
  renderGlobalLevelsPanel,
  renderEnvironmentPanel,
  orderedEnvironmentPresets,
  visibleLevelKeysForPreset,
  projectLevelDefaultProfile,
  projectLevelProfileSnapshot,
  projectLevelProfilesWithActive,
  syncActiveProjectLevelProfile,
  projectLevelProfileModified,
  projectLevelProfilesEqual,
  isLevelBoundaryBand,
  isCompactTechnicalLevel,
  isInputHeightTechnicalLevel,
  levelVisualStackHeight,
  levelVisualSegmentMinHeight,
  levelVisualSegmentFlex,
  renderLevelDetailSelect,
  renderLevelSegmentControls,
  renderLevelDetailOptions,
  renderLevelProfileReset,
  renderDoubleFloorColorOptions,
  bindGlobalLevelControls,
  renderScenePresetControls,
  beginProjectLevelFieldChange,
  finishProjectLevelFieldChange,
  updateProjectLevelVisualFilter,
  updateProjectLevelDetailField,
  updateProjectLevelTextureField,
  bindGlobalLevelTexturePickers,
  applyProjectEnvironmentPreset,
  escapeLevelHtml,
  updateProjectLevelValue,
  updateProjectLevelOption,
  resetActiveProjectLevelProfile,
  syncGlobalRoomHeightControl
});

export {
  GlobalLevels,
  GLOBAL_LEVEL_DEFINITIONS,
  GLOBAL_LEVEL_BOUNDARY_SEGMENT_VALUE,
  GLOBAL_LEVEL_BOUNDARY_MIN_HEIGHT,
  GLOBAL_LEVEL_BAND_MIN_HEIGHT,
  GLOBAL_LEVEL_INPUT_SEGMENT_MIN_HEIGHT,
  GLOBAL_LEVEL_TECH_COMPACT_MIN_HEIGHT,
  GLOBAL_LEVEL_CONTROL_MIN_HEIGHT,
  GLOBAL_LEVEL_STACK_HEIGHT,
  GLOBAL_LEVEL_VISUAL_DEFINITIONS,
  GLOBAL_LEVEL_VISUAL_FILTERS,
  renderGlobalLevelsPanel,
  renderEnvironmentPanel,
  orderedEnvironmentPresets,
  visibleLevelKeysForPreset,
  projectLevelDefaultProfile,
  projectLevelProfileSnapshot,
  projectLevelProfilesWithActive,
  syncActiveProjectLevelProfile,
  projectLevelProfileModified,
  projectLevelProfilesEqual,
  isLevelBoundaryBand,
  isCompactTechnicalLevel,
  isInputHeightTechnicalLevel,
  levelVisualStackHeight,
  levelVisualSegmentMinHeight,
  levelVisualSegmentFlex,
  renderLevelDetailSelect,
  renderLevelSegmentControls,
  renderLevelDetailOptions,
  renderLevelProfileReset,
  renderDoubleFloorColorOptions,
  bindGlobalLevelControls,
  renderScenePresetControls,
  beginProjectLevelFieldChange,
  finishProjectLevelFieldChange,
  updateProjectLevelVisualFilter,
  updateProjectLevelDetailField,
  updateProjectLevelTextureField,
  bindGlobalLevelTexturePickers,
  applyProjectEnvironmentPreset,
  escapeLevelHtml,
  updateProjectLevelValue,
  updateProjectLevelOption,
  resetActiveProjectLevelProfile,
  syncGlobalRoomHeightControl
};
